import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import axios from "axios"
import { FileItems, FileType } from "@pma/types"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { useInfiniteQuery } from "@tanstack/react-query"
import { SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import { FilePreview } from "../dashboard/preview-file"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@components/common/hover-card"
import { RenderFileIcon } from "./files-view"
import RestoreFile from "./restore-file"
import DownloadFile from "./download-file"
import { Separator } from "@components/common/separator"
import { Trash2 } from "lucide-react"

const DeletedFiles = () => {
  const { translate } = useTranslator()

  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const isOrganizationOwner = authConfig?.workspace?.owner_id === authConfig?.id
  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (company?.id === authConfig?.last_organization_id && company?.role?.id == 22 /* ADMIN_ROLE_ID */) return true
  })

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const getFiles = async ({ pageParam = 1 }) => {
    const response = await axios.get(
      SERVER_URL +
        `/files/getAll/byCurrentUser?deleted=true&paging_start_page=${pageParam}&paging_per_page=${12}&organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["deleted-files"],
    queryFn: ({ pageParam = 1 }) => getFiles({ pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return lastPage.nextPage
    },
  })

  const handleLoadMoreData = async () => {
    if (!isFetchingNextPage) {
      await fetchNextPage()
    }
  }

  const isEmpty = !data?.pages[0]?.itemsReceived
  const totalFiles = data?.pages[0]?.itemsTotal || 0

  if (error) {
    return (
      <Card className='flex h-[70vh] flex-col items-center justify-center gap-4'>
        <Trash2 className='h-12 w-12 text-muted-foreground' />
        <div className='text-center'>
          <p className='text-lg font-semibold text-destructive'>
            {translate("Error loading deleted files")}
          </p>
          <p className='text-sm text-muted-foreground'>{translate("Please try again later")}</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className='flex h-[70vh] flex-col'>
      <CardHeader className='flex-shrink-0 border-b bg-muted/50'>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2'>
            <Trash2 className='h-5 w-5 text-muted-foreground' />
            {translate("Deleted Files")}
          </CardTitle>
          {totalFiles > 0 && (
            <span className='text-sm text-muted-foreground'>
              {totalFiles} {translate("total files")}
            </span>
          )}
        </div>
      </CardHeader>
      <Separator />
      <CardContent className='flex-1 overflow-hidden p-6'>
        <div className='h-full overflow-y-auto'>
          {isLoading ? (
            <div className='flex h-full items-center justify-center'>
              <div className='space-y-4 text-center'>
                <PmsLoader />
                <p className='text-sm text-muted-foreground'>
                  {translate("Loading deleted files...")}
                </p>
              </div>
            </div>
          ) : isEmpty ? (
            <div className='flex h-full flex-col items-center justify-center gap-6 text-center'>
              <div className='relative'>
                <Trash2 className='h-20 w-20 text-muted-foreground/30' />
                <div className='absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-muted'>
                  <span className='text-xs text-muted-foreground'>0</span>
                </div>
              </div>
              <div className='max-w-sm space-y-2'>
                <h3 className='text-lg font-semibold text-muted-foreground'>
                  {translate("No deleted files found")}
                </h3>
                <p className='text-sm leading-relaxed text-muted-foreground/80'>
                  {translate(
                    "When you delete files, they will appear here and can be restored if needed",
                  )}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className='grid grid-cols-2 gap-4 pb-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6'>
                {data &&
                  data.pages?.map((page, pageIndex) =>
                    page?.items?.map((file: FileItems, fileIndex: number) => (
                      <div
                        key={`${pageIndex}-${file.id}`}
                        className='group relative flex flex-col items-center justify-center rounded-xl border border-border/40 bg-card/50 p-4 transition-all duration-200 hover:-translate-y-1 hover:border-primary/30 hover:bg-card hover:shadow-lg hover:shadow-primary/5'
                      >
                        <HoverCard>
                          <FilePreview file={file}>
                            <HoverCardTrigger className='flex h-full min-h-[100px] w-full flex-col items-center justify-center space-y-3'>
                              {/* File Icon */}
                              <div className='flex-shrink-0 transition-transform group-hover:scale-110'>
                                <RenderFileIcon size='lg' type={file?.type as FileType} />
                              </div>

                              {/* File Name */}
                              <div className='w-full px-2 text-center'>
                                <p
                                  className='text-[11px] font-medium leading-tight text-muted-foreground transition-colors group-hover:text-foreground'
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: "vertical",
                                    wordBreak: "break-word",
                                    hyphens: "auto",
                                    overflow: "hidden",
                                  }}
                                  title={file?.name}
                                >
                                  {file?.name}
                                </p>
                              </div>
                            </HoverCardTrigger>
                          </FilePreview>

                          {/* Hover Actions */}
                          <HoverCardContent className='flex w-fit items-center gap-2 p-2'>
                            <DownloadFile file={file} />
                            {file?.deleted &&
                            (isOrganizationOwner || isAdmin || file?.deleted_by == authConfig.id) ? (
                              <RestoreFile file={file} />
                            ) : null}
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    )),
                  )}
              </div>

              {/* Loading and Load More Section */}
              <div className='flex flex-col items-center gap-4 border-t border-border/50 pt-6'>
                {isFetchingNextPage && (
                  <div className='flex items-center gap-3 rounded-lg bg-muted/30 p-4'>
                    <PmsLoader />
                    <span className='text-sm font-medium text-muted-foreground'>
                      {translate("Loading more files...")}
                    </span>
                  </div>
                )}

                {hasNextPage && !isFetchingNextPage && (
                  <Button
                    variant='outline'
                    size='lg'
                    className='w-full max-w-xs transition-colors hover:bg-primary hover:text-primary-foreground'
                    onClick={handleLoadMoreData}
                    disabled={isFetchingNextPage}
                  >
                    {translate("Load More Files")}
                  </Button>
                )}

                {!hasNextPage && data?.pages && data.pages.length > 1 && (
                  <div className='flex items-center gap-2 text-muted-foreground'>
                    <div className='h-px flex-1 bg-border'></div>
                    <span className='rounded-full bg-muted/50 px-3 py-1 text-xs'>
                      {translate("All files loaded")}
                    </span>
                    <div className='h-px flex-1 bg-border'></div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default DeletedFiles
